/* ============================================================
   AXOMARKS — theme behaviour
   ------------------------------------------------------------
   Everything here replaces a React hook from the Next.js build:
   the header scroll state, the gallery, the quantity stepper,
   the variant picker and the cart. No framework, no dependencies.

   The rule throughout: the page must already work with this file
   blocked. Forms post normally, the first gallery photo shows,
   links navigate. This only upgrades the experience.
   ============================================================ */
(function () {
  'use strict';

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* The theme editor re-runs init() on every section render. Elements that
     survive that render would collect a second copy of every listener, so
     each one is stamped the first time it is wired and skipped after. */
  function claim(el, key) {
    if (!el) return false;
    var attr = 'axInit' + key;
    if (el.dataset[attr]) return false;
    el.dataset[attr] = '1';
    return true;
  }

  /* ---------- Header -------------------------------------------------
     Ports the scroll listener in Header.tsx: past 24px the sticky bar
     goes from 35% to 82% opaque and grows a bottom hairline. */
  function initHeader() {
    var header = $('[data-ax-header]');
    if (!claim(header, 'Header')) return;

    var hero = $('.ax-hero');

    var apply = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 24);

      // Drives the header wordmark's fade-in. "Past the hero" means the hero's
      // bottom edge has slid up under the header bar — measured live so it
      // stays right through resizes and the hero's vh-based height.
      if (hero) {
        header.classList.toggle('is-past-hero', hero.getBoundingClientRect().bottom <= header.offsetHeight);
      } else {
        header.classList.add('is-past-hero');
      }
    };

    apply();
    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply, { passive: true });
  }

  /* ---------- Collection sort ----------------------------------------
     Shopify reads the sort order from a `sort_by` query parameter, so the
     select just rewrites the URL. Other params (page, filters) are kept,
     but `page` is dropped: re-sorting should land you on page 1. */
  function initSort() {
    var select = $('[data-ax-sort]');
    if (!claim(select, 'Sort')) return;

    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ---------- Gallery ------------------------------------------------
     Ports Gallery.tsx. Every slide is in the DOM already; this moves the
     is-active class, wraps at both ends, and keeps the 01 / 09 counter
     in step. */
  function initGallery() {
    $$('[data-ax-gallery]').forEach(function (root) {
      if (!claim(root, 'Gallery')) return;
      var slides = $$('[data-ax-slide]', root);
      if (slides.length < 2) return;

      var thumbs = $$('[data-ax-gallery-go]', root);
      var counter = $('[data-ax-gallery-current]', root);
      var index = 0;

      var show = function (next) {
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
        });
        thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle('is-active', i === index);
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        if (counter) counter.textContent = ('0' + (index + 1)).slice(-2);
      };

      $$('[data-ax-gallery-step]', root).forEach(function (btn) {
        btn.addEventListener('click', function () {
          show(index + parseInt(btn.getAttribute('data-ax-gallery-step'), 10));
        });
      });

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          show(parseInt(thumb.getAttribute('data-ax-gallery-go'), 10));
        });
      });

      // Arrow keys only while the gallery has focus inside it, so they don't
      // fight the page scroll everywhere else.
      root.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { show(index - 1); }
        else if (event.key === 'ArrowRight') { show(index + 1); }
      });
    });
  }

  /* ---------- Quantity steppers --------------------------------------
     Ports the QtyStepper in ProductModal.tsx. `min` differs by context:
     1 on a product page, 0 on the cart (where 0 means remove), so it is
     read off the input instead of hard-coded. */
  function stepQuantity(input, delta) {
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = min;
    var next = Math.max(min, current + delta);
    if (next === current) return false;
    input.value = next;
    return true;
  }

  function initProductQuantity() {
    $$('[data-ax-qty]').forEach(function (btn) {
      if (!claim(btn, 'Qty')) return;
      btn.addEventListener('click', function () {
        var input = $('[data-ax-qty-input]', btn.closest('.ax-qty'));
        if (input) stepQuantity(input, parseInt(btn.getAttribute('data-ax-qty'), 10));
      });
    });
  }

  /* ---------- Variant picker -----------------------------------------
     The radios carry option values; a variant is the row whose `options`
     array matches all of them in order. Prices arrive pre-formatted from
     Liquid (see the note in sections/main-product.liquid). */
  function initVariants() {
    var wrap = $('[data-ax-variants]');
    var dataEl = $('[data-ax-variant-data]');
    if (!wrap || !dataEl || !claim(wrap, 'Variants')) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (err) {
      return; // Leave the server-rendered variant selected.
    }

    var idInput = $('[data-ax-variant-id]');
    var priceEl = $('[data-ax-price]');
    var compareEl = $('[data-ax-compare]');
    var addBtn = $('[data-ax-add]');
    var addLabel = $('[data-ax-add-label]');

    var update = function () {
      var chosen = $$('input[type="radio"]:checked', wrap)
        .sort(function (a, b) {
          return a.getAttribute('data-ax-option-index') - b.getAttribute('data-ax-option-index');
        })
        .map(function (input) { return input.value; });

      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var options = variants[i].options || [];
        if (options.length !== chosen.length) continue;
        var same = true;
        for (var j = 0; j < chosen.length; j++) {
          if (options[j] !== chosen[j]) { same = false; break; }
        }
        if (same) { match = variants[i]; break; }
      }

      if (idInput) idInput.value = match ? match.id : '';
      if (priceEl && match) priceEl.innerHTML = match.price;

      if (compareEl) {
        if (match && match.compare_at) {
          compareEl.innerHTML = match.compare_at;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (addBtn) {
        // A combination with no variant at all reads as unavailable, which is
        // what it is — there is nothing to buy.
        var buyable = !!(match && match.available);
        addBtn.disabled = !buyable;
        if (addLabel) {
          if (buyable) {
            addLabel.textContent = addBtn.getAttribute('data-label-add');
          } else if (match) {
            addLabel.textContent = addBtn.getAttribute('data-label-soldout');
          } else {
            addLabel.textContent = addBtn.getAttribute('data-label-unavailable');
          }
        }
      }
    };

    wrap.addEventListener('change', update);
    update();
  }

  /* ---------- Cart count --------------------------------------------- */
  function setCartCount(count) {
    $$('[data-ax-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function refreshCartCount() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) { setCartCount(cart.item_count); })
      .catch(function () { /* the badge just stays stale until the next load */ });
  }

  /* ---------- Add to cart --------------------------------------------
     Intercepts the product form so adding an item doesn't throw the
     customer onto the cart page — the header badge ticks up and the
     button confirms, same as the old modal did. If the request fails the
     form is submitted for real so the browser shows Shopify's error. */
  function initAddToCart() {
    var form = $('#ax-product-form');
    if (!claim(form, 'AddToCart')) return;

    var btn = $('[data-ax-add]', form);
    var label = $('[data-ax-add-label]', form);
    var resetTimer = null;

    form.addEventListener('submit', function (event) {
      if (!btn || btn.disabled) return;
      event.preventDefault();

      if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
      btn.disabled = true;
      if (label) label.textContent = btn.getAttribute('data-label-adding');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          return refreshCartCount();
        })
        .then(function () {
          if (label) label.textContent = btn.getAttribute('data-label-added');
          btn.disabled = false;
          resetTimer = setTimeout(function () {
            if (label) label.textContent = btn.getAttribute('data-label-add');
          }, 2600);
        })
        .catch(function () {
          // Hand it back to the browser: a real POST surfaces the actual
          // reason (out of stock, quantity limit) on Shopify's own page.
          btn.disabled = false;
          form.submit();
        });
    });
  }

  /* ---------- Cart page ---------------------------------------------
     Replaces cart-context.tsx. /cart/change.js is asked to re-render the
     `main-cart` section as well, so totals, discounts and line prices all
     come back correct from Liquid instead of being recomputed here. */
  function initCartPage() {
    var form = $('[data-ax-cart-form]');
    if (!claim(form, 'CartPage')) return;

    var busy = false;

    var changeLine = function (line, quantity) {
      if (busy) return;
      busy = true;
      form.style.opacity = '0.55';
      form.style.pointerEvents = 'none';

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: 'main-cart',
          sections_url: window.location.pathname
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('change failed');
          return res.json();
        })
        .then(function (cart) {
          setCartCount(cart.item_count);
          var html = cart.sections && cart.sections['main-cart'];
          var host = document.getElementById('shopify-section-main-cart');
          if (!html || !host) { window.location.reload(); return; }

          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.getElementById('shopify-section-main-cart');
          host.innerHTML = fresh ? fresh.innerHTML : html;
          busy = false;
          bindCartControls();
        })
        .catch(function () {
          window.location.reload();
        });
    };

    var bindCartControls = function () {
      var scope = document.getElementById('shopify-section-main-cart') || document;

      $$('[data-ax-cart-qty]', scope).forEach(function (btn) {
        if (!claim(btn, 'CartQty')) return;
        btn.addEventListener('click', function () {
          var row = btn.closest('[data-ax-line]');
          var input = $('[data-ax-cart-qty-input]', row);
          if (!input) return;
          var delta = parseInt(btn.getAttribute('data-ax-cart-qty'), 10);
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
          changeLine(parseInt(row.getAttribute('data-ax-line'), 10), next);
        });
      });

      $$('[data-ax-cart-remove]', scope).forEach(function (btn) {
        if (!claim(btn, 'CartRemove')) return;
        btn.addEventListener('click', function () {
          var row = btn.closest('[data-ax-line]');
          changeLine(parseInt(row.getAttribute('data-ax-line'), 10), 0);
        });
      });

      // A typed quantity commits on blur / Enter rather than per keystroke,
      // so "12" is not read as a 1 followed by a 12.
      $$('[data-ax-cart-qty-input]', scope).forEach(function (input) {
        if (!claim(input, 'CartInput')) return;
        var commit = function () {
          var row = input.closest('[data-ax-line]');
          var next = Math.max(0, parseInt(input.value, 10) || 0);
          changeLine(parseInt(row.getAttribute('data-ax-line'), 10), next);
        };
        input.addEventListener('change', commit);
        input.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') { event.preventDefault(); commit(); }
        });
      });
    };

    bindCartControls();
  }

  /* ---------- Boot --------------------------------------------------- */
  function init() {
    initHeader();
    initSort();
    initGallery();
    initProductQuantity();
    initVariants();
    initAddToCart();
    initCartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The theme editor swaps section markup in and out without a page load, so
  // anything bound above has to be re-bound against the fresh DOM.
  document.addEventListener('shopify:section:load', init);
})();
